import pc from "picocolors";

export type AppErrorMessage = string;

export enum AppErrorReason {
    "ENV_MISSING_VALUE" = "ENV_MISSING_VALUE",
    "ENV_BAD_VALUE" = "ENV_BAD_VALUE",
    "STORY_MISSING_DATA" = "STORY_MISSING_DATA",
    "STORY_MOVED" = "STORY_MOVED",
    "STORY_NOT_FOUND" = "STORY_NOT_FOUND",
    "STORY_NOT_HYDRATED" = "STORY_NOT_HYDRATED",
    "UNEXPECTED_PARAMS" = "UNEXPECTED_PARAMS",
    "UNAUTHORIZED" = "UNAUTHORIZED",
    "SUBRESOURCE_LOCK" = "SUBRESOURCE_LOCK",
    "TRANSLATION_RATE_LIMITED" = "TRANSLATION_RATE_LIMITED",
    "INVALID_TRANSLATION" = "INVALID_TRANSLATION",
}

export interface AppErrorContext {
    reason: AppErrorReason;
    message?: string;
    context?: {
        [key: string]: unknown;
    };
}

export type WardenclyffeErrorConfig = {
    message: RegExp;
    path: RegExp;
    upstreamErrorCode?: number[];
    responseCode?: number;
    code?: number[];
};

export type AppErrorContextWithoutReason = Omit<AppErrorContext, "reason">;

const isDevelopment = process.env.NODE_ENV === "development";

export function createUnauthorizedError(message: string) {
    return new UnauthorizedError({
        message,
    });
}

export function createSubresourceLockError(message: string) {
    return new AppError({
        reason: AppErrorReason.SUBRESOURCE_LOCK,
        message,
    });
}

export function createStoryMissingDataError(
    message: string,
    missingFields: string[]
) {
    return new StoryMissingDataError({
        message,
        context: { missingFields },
    });
}

export function createStoryMovedError(message: string, redirectUrl: string) {
    return new StoryMovedError({
        message,
        context: { redirectUrl },
    });
}

export function createStoryNotFoundError(message: string) {
    return new StoryNotFoundError({
        message,
    });
}

// TODO: [ENG4WCP-5161] add tests
export function createTranslationRateLimitingError(message?: string) {
    return new TranslationRateLimitingError({
        message,
    });
}

export function createInvalidTranslationError(message?: string) {
    return new InvalidTranslationError({
        message,
    });
}

export function createStoryNotHydratedError(message: string) {
    return new AppError({
        reason: AppErrorReason.STORY_NOT_HYDRATED,
        message,
    });
}

export function createUnexpectedParamsError(message: string) {
    return new AppError({
        reason: AppErrorReason.UNEXPECTED_PARAMS,
        message,
    });
}

export function getIsStoryNotFoundError(error: unknown) {
    return (
        error instanceof AppError &&
        error.reason === AppErrorReason.STORY_NOT_FOUND
    );
}

export function getIsUnexpectedParamsError(error: unknown) {
    return (
        error instanceof AppError &&
        error.reason === AppErrorReason.UNEXPECTED_PARAMS
    );
}

export function getIsUnauthorizedError(error: unknown) {
    return (
        error instanceof AppError &&
        error.reason === AppErrorReason.UNAUTHORIZED
    );
}

export function getIsSubresourceLockError(error: unknown) {
    return (
        error instanceof AppError &&
        error.reason === AppErrorReason.SUBRESOURCE_LOCK
    );
}

export function getIsStoryMovedError(error: unknown) {
    return (
        error instanceof AppError && error.reason === AppErrorReason.STORY_MOVED
    );
}

const formatMessage = (context: AppErrorContext) => {
    if (isDevelopment) {
        return `\n\n${pc.bgYellow(
            pc.black(JSON.stringify(context, null, 2))
        )}\n\n`;
    }

    return JSON.stringify(context);
};

export class AppError extends Error {
    reason: AppErrorReason;
    constructor(context: AppErrorContext) {
        super(formatMessage(context));
        if (context.message) {
            this.message = JSON.stringify(context.message);
        }
        this.reason = context.reason;
    }
}

export class UnauthorizedError extends AppError {
    constructor(context?: AppErrorContextWithoutReason) {
        super({
            ...context,
            reason: AppErrorReason.UNAUTHORIZED,
        });
    }
}

export class StoryNotFoundError extends AppError {
    constructor(context?: AppErrorContextWithoutReason) {
        super({
            ...context,
            reason: AppErrorReason.STORY_NOT_FOUND,
        });
    }
}

export class StoryMovedError extends AppError {
    redirectUrl: string;

    constructor(
        context: AppErrorContextWithoutReason & {
            context: { redirectUrl: string };
        }
    ) {
        super({ ...context, reason: AppErrorReason.STORY_MOVED });
        this.redirectUrl = context.context.redirectUrl;
    }
}

export class StoryMissingDataError extends AppError {
    constructor(
        context: AppErrorContextWithoutReason & {
            context: { missingFields: string[] };
        }
    ) {
        super({ ...context, reason: AppErrorReason.STORY_MISSING_DATA });
    }
}

export class TranslationRateLimitingError extends AppError {
    constructor(context?: AppErrorContextWithoutReason) {
        super({
            ...context,
            reason: AppErrorReason.TRANSLATION_RATE_LIMITED,
        });
    }
}

export class InvalidTranslationError extends AppError {
    constructor(context?: AppErrorContextWithoutReason) {
        super({
            ...context,
            reason: AppErrorReason.INVALID_TRANSLATION,
        });
    }
}

export type TranslationError =
    | TranslationRateLimitingError
    | InvalidTranslationError;
